import { Col, Divider, Popover, Row, Skeleton, Space } from "antd";
import React, { useEffect, useState } from "react";
import Text from "../../../components/Text/Text";
import icon from "../../../resource/icon";
import Input from "../../../components/Input/Input";
import { SearchOutlined, UnorderedListOutlined } from "@ant-design/icons";
import Select from "../../../components/Select/Select";
import TablePagination from "../../../components/Table/TablePagination";
import { FlexCol } from "../../../components/Container/Container";
import { dateFormatter, numberFormatter } from "../../../utility/Formatter";
import { ActionBtn } from "../../../components/Button/TableActionButton";
import { useNavigate, useParams } from "react-router-dom";
import { getFollowAchieve, getFollowAchieveShop } from "../../../datasource/AchievementDataSource";
import { useEffectOnce } from "react-use";
import {
  followAchievementShop_INIT,
  type followAchievement,
  type followAchievementShop,
} from "../../../entities/AchievementEntity";
import ZoneSelect from "../../../components/Select/ZoneSelect";
import { CircleDot } from "../../CRMPage/ApprovePoint/IndexApprovePoint";
import SortFieldCustom from "../../../components/SortFieldTable/SortFieldCustom";
import useSorting from "../../../hook/useSorting";

interface OptionType {
  label: string;
  value: string;
  key: string;
}
function AchievePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const company = JSON.parse(localStorage.getItem("company")!);
  const [detail, setDetail] = useState<followAchievement>();
  const [mileStoneName, setMileStoneName] = useState<OptionType[]>([]);
  const [mileStoneSpecialName, setMileStoneSpecialName] = useState<OptionType[]>([]);
  const [shop, setShop] = useState<{ data: followAchievementShop[]; achievementShopCount: number }>(
    {
      data: [followAchievementShop_INIT],
      achievementShopCount: 0,
    },
  );
  const { sortField, sortDirection, handleSortChange } = useSorting();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    selectedZone: undefined as string | undefined,
    searchText: undefined as string | undefined,
    searchMileStone: undefined as string | undefined,
    searchMileStoneSpecial: undefined as string | undefined,
    type: undefined as string | undefined,
  });
  const fetchDetail = async () => {
    try {
      await getFollowAchieve(id!).then((res) => {
        setDetail(res);
        const rankData = res?.mainRewardAll.map((item: any, index: number) => ({
          label: item,
          value: item,
          key: index,
        }));
        setMileStoneName(rankData);
        const specialData = res?.specialRewardAll.map((item: any, index: number) => ({
          label: item,
          value: item,
          key: index,
        }));
        setMileStoneSpecialName(specialData);
      });
    } catch (error) {
      console.log(error);
    }
  };

  const fetchShopList = async () => {
    setLoading(true);
    try {
      await getFollowAchieveShop({
        company: company?.companyCode,
        achievementId: id,
        page: filters.page,
        take: 10,
        searchText: filters.searchText,
        customerType: filters.type,
        customerZone: filters.selectedZone,
        mileStoneName: filters.searchMileStone,
        mileStoneSpecialName: filters.searchMileStoneSpecial,
        sortBy: sortDirection ? sortField : undefined,
        sortDirection: sortDirection,
      }).then((res) => {
        setShop(res);
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  useEffectOnce(() => {
    fetchDetail();
  });
  useEffect(() => {
    fetchShopList();
  }, [filters, sortField, sortDirection]);

  const startDateStr = detail?.startDate ? dateFormatter(detail.startDate) : "-";
  const endDateStr = detail?.endDate ? dateFormatter(detail.endDate) : "-";
  const daysPassedStr =
    detail?.daysPassed && detail.daysPassed > 0
      ? `(ผ่านมาแล้ว ${numberFormatter(Number(detail.daysPassed), 0)} วัน)`
      : "";
  const details: any[] = [
    { label: "ชื่อความสำเร็จ", value: detail?.achievementName },
    {
      label: "ระยะเวลาแคมเปญ",
      value: `${startDateStr} - ${endDateStr} ${daysPassedStr}`.trim(),
      icon: icon.crmCalendar,
    },
    {
      label: "เงื่อนไขความสำเร็จ",
      value: `ยอดซื้อสะสม ${detail?.conditionsToAchieve === "THB" ? "(บาท)" : "(จำนวน)"}`,
      icon: icon.crmGoal,
    },
    {
      label: "ร้านค้าที่เข้าร่วม",
      value: `${numberFormatter(detail?.achievementShopCount ?? 0, 0)} ร้าน`,
      icon: icon.crmStore,
    },
  ];
  const columns: any = [
    {
      title: () => (
        <SortFieldCustom
          title='ชื่อร้าน'
          sortDirection={sortField === "customerName" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("customerName")}
        />
      ),
      dataIndex: "customerName",
      key: "customerName",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text fontFamily='Sarabun'>{value}</Text>
              <Text
                level={6}
                color='Text3'
                fontFamily='Sarabun'
                style={{ display: "flex", alignItems: "center" }}
              >
                {row.customerNo} {row.customerNo || row.customerZone ? <CircleDot /> : null}
                {row.customerType}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "เขต",
      dataIndex: "customerZone",
      key: "customerZone",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text fontFamily='Sarabun'>{value}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ระดับการสะสม",
      dataIndex: "currentMilestoneName",
      key: "currentMilestoneName",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text fontFamily='Sarabun'>{value || "-"}</Text>
              <Text level={6} color='Text3' fontFamily='Sarabun'>
                {row.currentRewardNum && "ระดับที่ " + row.currentRewardNum}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "รางวัลพิเศษ",
      dataIndex: "currentSpecialMilestoneName",
      key: "currentSpecialMilestoneName",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              {row?.isSpecial ? (
                <>
                  <Text fontFamily='Sarabun'>{value ?? "-"}</Text>
                  <Text level={6} color='Text3' fontFamily='Sarabun'>
                    {row.currentSpecialRewardNum && "รางวัลพิเศษ" + row.currentSpecialRewardNum}
                  </Text>
                </>
              ) : (
                "-"
              )}
            </FlexCol>
          ),
        };
      },
    },
    {
      title: () => (
        <SortFieldCustom
          title='ยอดซื้อสะสม'
          sortDirection={sortField === "progressValue" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("progressValue")}
        />
      ),
      dataIndex: "progressValue",
      key: "progressValue",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{numberFormatter(value, 2)}</Text>
              <Text level={6} color='Text3' fontFamily='Sarabun'>
                บาท
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: () => (
        <SortFieldCustom
          title='ยอดซื้อเพื่อระดับถัดไป	'
          sortDirection={sortField === "remainingToNext" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("remainingToNext")}
        />
      ),
      dataIndex: "remainingToNextCal",
      key: "remainingToNextCal",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{numberFormatter(value, 2)}</Text>
              <Text level={6} color='Text3' fontFamily='Sarabun'>
                บาท
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <ActionBtn
              onClick={() =>
                navigate("/crm/achievement/shop-detail/" + id, {
                  state: {
                    shop: row,
                  },
                })
              }
              icon={<UnorderedListOutlined style={{ width: 16, height: 16, fontWeight: 700 }} />}
            />
          ),
        };
      },
    },
  ];

  return (
    <>
      <div style={{ margin: "12px 0px 18px 0px" }}>
        <Text fontSize={16} fontWeight={700}>
          ภาพรวมติดตามความสำเร็จ
        </Text>
      </div>
      {details.map((item, index) =>
        !detail ? (
          <Skeleton title paragraph={false} active={true} key={index} />
        ) : (
          <Row key={index} style={{ marginBottom: "6px" }}>
            <Col span={6}>
              <Space align='center'>
                {item.icon && <img src={item.icon} style={{ width: 18, height: 18 }} />}
                <Text fontFamily='Sarabun' fontSize={16} fontWeight={500} color='Text3'>
                  {item.label}
                </Text>
              </Space>
            </Col>
            <Col span={18}>
              <Text fontFamily='Sarabun' fontSize={16}>
                {item.value}
              </Text>
            </Col>
          </Row>
        ),
      )}

      <Divider />
      <Space wrap size={4} direction='horizontal'>
        <img src={icon.store} style={{ width: 18, height: 18, marginRight: "8px" }} />
        <Text fontSize={16} fontWeight={700}>
          รายละเอียดรายร้านค้า
        </Text>
      </Space>

      <Row gutter={10} style={{ margin: "18px 0px 20px 0px" }}>
        <Col span={6}>
          <Input
            allowClear
            placeholder='รหัสร้านค้า/ชื่อร้านค้า'
            suffix={<SearchOutlined />}
            defaultValue={filters.searchText}
            onPressEnter={(e) => {
              const value = (e.target as HTMLTextAreaElement).value;
              setFilters((prev) => ({
                ...prev,
                searchText: value,
                page: 1,
              }));
            }}
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              if (!value) {
                setFilters((prev) => ({
                  ...prev,
                  searchText: undefined,
                }));
              }
            }}
          />
        </Col>
        <Col span={4}>
          <Select
            data={[
              { label: "Dealer", value: "DEALER", key: "DEALER" },
              { label: "Sub Dealer", value: "SUB_DEALER", key: "SUB_DEALER" },
            ]}
            placeholder='ประเภทคู่ค้าทั้งหมด'
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                type: e,
                page: 1,
              }))
            }
          />
        </Col>
        <Col span={3}>
          <ZoneSelect
            value={filters.selectedZone}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                selectedZone: e,
                page: 1,
              }))
            }
          />
        </Col>
        <Col span={3}>
          <Select
            data={mileStoneName}
            placeholder='ระดับทั้งหมด'
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                searchMileStone: e,
                page: 1,
              }))
            }
          />
        </Col>
        <Col span={3}>
          <Select
            data={mileStoneSpecialName}
            placeholder='รางวัลพิเศษทั้งหมด'
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                searchMileStoneSpecial: e,
                page: 1,
              }))
            }
          />
        </Col>
        <Col span={4} style={{ marginLeft: "auto", textAlign: "right" }}>
          <Text fontSize={14}>ทั้งหมด {numberFormatter(shop.achievementShopCount, 0)} ร้าน</Text>
        </Col>
      </Row>

      <TablePagination
        columns={columns}
        data={shop.data || []}
        isLoading={loading}
        locale={{
          emptyText: (
            <div
              style={{
                padding: 32,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 24,
                minHeight: 350,
              }}
            >
              <img
                src={icon.emptyHistoryPoint}
                style={{
                  width: 80,
                  height: 90,
                }}
                alt='empty_table'
              />
              <Text fontFamily='Sarabun' fontSize={14} fontWeight={400} color='Text3'>
                ไม่มีรายการความสำเร็จ
              </Text>
            </div>
          ),
        }}
        pagination={{
          current: filters.page,
          total: shop.achievementShopCount,
          pageSize: 10,
        }}
        onPageChange={(pageNum) =>
          setFilters((prev) => ({
            ...prev,
            page: pageNum,
          }))
        }
      />
    </>
  );
}

export default AchievePage;
